import type { SUUID } from 'short-uuid'
import type { CoordinationData, ValidRingSequence, ControllerData, PhaseData } from '@app/models/optimus-configuration'
import type { IOptimusTod } from '@shared-types/optimus.js'

import client from '@shared/utils/client-rest'
// import { fetchCache } from '@shared/utils/fetch-cache'
import { generateTodDescription } from '@app/helpers/att-args'
// import { minimizeID } from '@client-shared/utils/uuid-compression'
import {
	getIsOptimusEnabledSchema,
	getOptimusJobSplitAnalysisSchema,
	getOptimusUpdateJobFlagsSchema,
	getOptimusUpdateJobNameSchema,
	getOptimusV2JobDetailsSchema,
	getOptimusV2JobSchema,
	type job_status_schema,
	type getOptimusV2SignalSchema,
	type submitOptimusJobBodySchema,
	type optimus_deployed_resolution_schema,
	type optimus_overlapping_plans_schema,
} from '../../../shared-schemas/optimus/optimus-jobs'
import * as v from 'valibot'
import type { getDeployJobResponseSchema } from '../../../shared-schemas/optimus/optimus-actions'
import { query } from '@client-shared/utils/fetch-cache'
import { getTimingPlanMonitorSchema } from '../../../shared-schemas/optimus/optimus-reports'

export type IIsOptimusEnabled = v.InferOutput<ReturnType<typeof getIsOptimusEnabledSchema>>

export type IOptimusJob = v.InferOutput<ReturnType<typeof getOptimusV2JobSchema>>

export type IOptimusJobSignal = v.InferOutput<ReturnType<typeof getOptimusV2SignalSchema>>

export type IOptimusDetailedJob = v.InferOutput<ReturnType<typeof getOptimusV2JobDetailsSchema>>

export type IOptimusJobStatus = v.InferOutput<typeof job_status_schema>

export type IOptimusDeployJobRes = v.InferOutput<ReturnType<typeof getDeployJobResponseSchema>>

export type ITimingPlanMonitor = v.InferOutput<ReturnType<typeof getTimingPlanMonitorSchema>>

export type SubmitOptimusJobBody = v.InferOutput<ReturnType<typeof submitOptimusJobBodySchema>>

export const OptimusReportRequestKeys = {
	allowed_date_ranges: (network_id: SUUID, tod: IOptimusTod) =>
		`optimus_report_request:allowed_date_ranges:network:${network_id}:tod:${generateTodDescription(tod)}`,
	full_set_documents: (
		network_id: SUUID,
		tod: IOptimusTod,
		timezone_iana: string,
		date_start: string,
		date_end: string,
	) =>
		`optimus_report_request:get_full_set_documents:network:${network_id}:tod:${generateTodDescription(tod)}:${timezone_iana}${date_start}${date_end}`,
}

export type AllowedDateRangesResponse = [string, string][] // Each string is 'YYYY-MM-DD' | 'infinity'

export function fetchAllowedDateRanges(
	network_id: string,
	tod: IOptimusTod,
	timezone_iana: string,
): Promise<AllowedDateRangesResponse> {
	return client(`/api/v1/pages/optimus-v2/allowed-valid-ranges/${network_id}`, {
		params: {
			tod_params: {
				has_monday: tod.time_of_day_data.days.monday,
				has_tuesday: tod.time_of_day_data.days.tuesday,
				has_wednesday: tod.time_of_day_data.days.wednesday,
				has_thursday: tod.time_of_day_data.days.thursday,
				has_friday: tod.time_of_day_data.days.friday,
				has_saturday: tod.time_of_day_data.days.saturday,
				has_sunday: tod.time_of_day_data.days.sunday,
				local_start_time: tod.local_start_time,
				local_end_time: tod.local_end_time,
				timezone_iana: timezone_iana,
			},
		},
	})
}

export interface SignalConfigurationTODSlice {
	has_flowlabs_supplemental_params: boolean
	// The configured TOD name (e.g. "AM Peak") from the signal's TOD schedule, or null when the
	// slice has no covering TOD.
	name: string | null
	days: {
		sunday: boolean
		monday: boolean
		tuesday: boolean
		wednesday: boolean
		thursday: boolean
		friday: boolean
		saturday: boolean
	}
	months: {
		january: boolean
		february: boolean
		march: boolean
		april: boolean
		may: boolean
		june: boolean
		july: boolean
		august: boolean
		september: boolean
		october: boolean
		november: boolean
		december: boolean
	} | null
	days_of_month: Record<string, boolean> | null
	local_start_time: string // hh:mm:ss
	local_end_time: string // hh:mm:ss

	phase_settings_id: SUUID | null
	phase_settings_external_id: string | null
	phase_settings_data: Record<string, Partial<PhaseData>> | null
	phase_settings_valid_range: string | null
	coordination_settings_id: SUUID | null
	coordination_settings_external_id: string | null
	coordination_settings_data: Partial<CoordinationData> | null
	coordination_settings_valid_range: string | null
	sequence_data: Partial<{
		phaseSequence: {
			ring_1: ValidRingSequence
			ring_2: ValidRingSequence
		}
	}> | null
	controller_settings_data: Partial<ControllerData>
}

export interface SignalConfigurationDateRangeSlice {
	[validfrom_validto: string]: SignalConfigurationTODSlice[] // Key Format - `YYYY-MM-DD|YYYY-MM-DD`
}
export type ExistingSignalConfigurationSlicesResponse = Record<string, SignalConfigurationDateRangeSlice>

export async function fetchExistingSignalConfigurationSlices(network_id: string) {
	return client(`/api/v1/pages/optimus-v2/slices/${network_id}`)
	// // BEGIN TEMP HACK
	// .then(res => {
	// 	const res_with_suuid = {}
	// 	for (let uuid in res) {
	// 		res_with_suuid[minimizeID(uuid)] = res[uuid]
	// 	}
	// 	return res_with_suuid
	// })
	// // END TEMP HACK
}

export type IDeployedResolution = v.InferOutput<typeof optimus_deployed_resolution_schema>
export type DeployedParamGroup = keyof IDeployedResolution

export type IOverlappingPlans = v.InferOutput<typeof optimus_overlapping_plans_schema>
export type IOverlappingPlanGroup = IOverlappingPlans[DeployedParamGroup]
export type IOverlappingPlanCandidate = IOverlappingPlanGroup['candidates'][number]
export type OverlappingPlansBySignalId = Record<string, IOverlappingPlans>

export interface IDeployedResolutionEntry {
	cycle: number | null
	offset: { offset: number | null; offset_reference_point: string | null; force_off_mode: string | null }
	splits: Record<string, number> | null
	sequence: unknown | null
	mode: string | null
	resolution: IDeployedResolution
	violating_phases: number[]
}

export type DeployedResolutionResponse = Record<string, IDeployedResolutionEntry>

// Mirrors the backend's is_missing_params: any ambiguous group blanks the whole deployed baseline, so
// a signal excluded from optimization has nothing left for the optimizer to hold fixed. Returns false
// while the verdict is still loading — the window is unknown, not known-bad.
export function hasUnresolvedDeployedParams(
	deployed_resolution: DeployedResolutionResponse | null | undefined,
	signal_id: string,
): boolean {
	if (!deployed_resolution) return false
	const resolution = deployed_resolution[signal_id]?.resolution
	if (!resolution) return true
	return Object.values(resolution).some((param) => param.status === 'inferred')
}

const TOD_DAY_KEYS = [
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
	'sunday',
] as const

// Pre-submit verdict on which signals have an unambiguous deployed baseline for this exact job
// window. Mirrors what the submit path persists, so the modal's asterisks match the job report's.
export function fetchDeployedResolution(
	network_id: string,
	tod: IOptimusTod,
	date_start: string,
	date_end: string,
	excluded_dates: string[] = [],
): Promise<DeployedResolutionResponse> {
	const days = tod.time_of_day_data.days
	return client(`/api/v1/pages/optimus-v2/deployed-resolution/${network_id}`, {
		params: {
			local_start_time: tod.local_start_time,
			local_end_time: tod.local_end_time,
			days: TOD_DAY_KEYS.filter((day) => days[day]).join(','),
			date_start,
			date_end,
			excluded_dates: excluded_dates.join(','),
		},
	})
}

export type FullSetDocumentsResponse = unknown

export function fetchFullSetDocuments(
	network_id: SUUID,
	tod: IOptimusTod,
	timezone_iana: string,
	date_start: string,
	date_end: string,
) {
	return client(`/api/v1/pages/optimus-v2/tms-data/${network_id}`, {
		params: {
			tod_params: {
				has_monday: tod.time_of_day_data.days.monday,
				has_tuesday: tod.time_of_day_data.days.tuesday,
				has_wednesday: tod.time_of_day_data.days.wednesday,
				has_thursday: tod.time_of_day_data.days.thursday,
				has_friday: tod.time_of_day_data.days.friday,
				has_saturday: tod.time_of_day_data.days.saturday,
				has_sunday: tod.time_of_day_data.days.sunday,
				local_start_time: tod.local_start_time,
				local_end_time: tod.local_end_time,
				timezone_iana: timezone_iana,
			},
			date_range: {
				start_date: date_start,
				end_date: date_end,
			},
		},
	})
}

// TODO: Add in checks for minimum required params

export function fetchIsOptimusEnabledFn(network_id: string): Promise<IIsOptimusEnabled> {
	return client(`/api/v1/pages/optimus-v2/status/${network_id}`).then(
		(data) => v.safeParse(getIsOptimusEnabledSchema(), data).output as IIsOptimusEnabled,
	)
}

// The jobs list is mutated by submit/clone/delete, so it must reflect the latest state on every fetch.
// A unique query param busts both the browser HTTP cache and the server response cache (Redis), which
// otherwise serve a stale list after a delete/refresh until a hard reload.
export function fetchOptimusV2CustomerJobs(customer_id: string): Promise<IOptimusJob[]> {
	return client(`/api/v1/pages/optimus-v2/jobs/${customer_id}?_=${Date.now()}`).then(
		(data) => v.safeParse(v.array(getOptimusV2JobSchema({ expected_format: 'short' })), data).output as IOptimusJob[],
	)
}

export function fetchOptimusV1CustomerJobs(customer_id: string): Promise<IOptimusJob[]> {
	return client(`/api/v1/pages/optimus/jobs-per-customer/${customer_id}?_=${Date.now()}`).then(
		(data) => v.safeParse(v.array(getOptimusV2JobSchema({ expected_format: 'short' })), data).output as IOptimusJob[],
	)
}

export function updateJobNameFn(job_id: string, job_name: string) {
	return client(`/api/v1/pages/optimus-v2/update-job-name/${job_id}`, {
		method: 'PUT',
		body: { job_name },
	}).then((data) => v.safeParse(getOptimusUpdateJobNameSchema({ expected_format: 'short' }), data).output)
}

export function updateJobFlagsFn(job_id: string, flags: { is_visible?: boolean; is_remote?: boolean }) {
	return client(`/api/v1/pages/optimus-v2/update-job-flags/${job_id}`, {
		method: 'PUT',
		body: flags,
	}).then((data) => v.safeParse(getOptimusUpdateJobFlagsSchema({ expected_format: 'short' }), data).output)
}

export function fetchOptimusJobDetailsFn(job_id: string, version: number): Promise<IOptimusDetailedJob> {
	const api_endpoint =
		version === 2 ? `/api/v1/pages/optimus-v2/job-details/${job_id}` : `/api/v1/pages/optimus/job-details/${job_id}`

	return client(api_endpoint).then((data) => {
		const result = v.safeParse(getOptimusV2JobDetailsSchema({ expected_format: 'short', convert_format: false }), data)
		// if (!result.success) {
		// 	for (const issue of result.issues) {
		// 		const path = issue.path?.map((p) => p.key).join('.') ?? 'unknown'
		// 		console.error(`[Optimus Schema] ${path}: ${issue.message} (received: ${JSON.stringify(issue.input)})`)
		// 	}
		// 	throw new v.ValiError(result.issues)
		// }
		return result.output as IOptimusDetailedJob
	})
}

export function fetchSplitAnalysisDataFn(job_id: string) {
	return client(`/api/v1/pages/optimus-v2/split-analysis-data/${job_id}`).then(
		(data) => v.safeParse(getOptimusJobSplitAnalysisSchema(), data).output,
	)
}

export function fetchCoordinationComparisonFn(job_id: string) {
	return client(`/api/v1/pages/optimus-v2/coordination-split-comparison/${job_id}`).then(
		(data) => v.safeParse(getOptimusJobSplitAnalysisSchema(), data).output,
	)
}

export interface ITimingPlanMonitorMetricsParams {
	customer_id: string
	deployment_date: string
	tod_params: {
		has_monday: boolean
		has_tuesday: boolean
		has_wednesday: boolean
		has_thursday: boolean
		has_friday: boolean
		has_saturday: boolean
		has_sunday: boolean
		local_start_time: string
		local_end_time: string
		timezone_iana: string
	}
}

export function fetchTimingPlanMonitorMetricsFn(network_id: string, params: ITimingPlanMonitorMetricsParams) {
	return client(`/api/v1/pages/network/${network_id}/timing-plan-monitor-metrics`, { params }).then(
		(data) => v.safeParse(getTimingPlanMonitorSchema(), data).output,
	)
}

export function fetchTimingPlanMonitorMetrics(network_id: string, params: ITimingPlanMonitorMetricsParams) {
	const fetch_fn = async () => client(`/api/v1/pages/network/${network_id}/timing-plan-monitor-metrics`, { params })
	const cache_key = `timing-plan-monitor-metrics:${network_id}:${JSON.stringify(params)}`
	return query(cache_key, fetch_fn)
}

export function submitOptimusJob(job_body: SubmitOptimusJobBody) {
	return client(`/api/v1/pages/optimus-v2`, {
		method: 'POST',
		body: job_body,
	})
}

export interface IOptimusNetworkRoute {
	route_id: string
	azimuth_degrees: number | null
	direction: 'NB' | 'SB' | 'EB' | 'WB' | null
}

export function fetchOptimusNetworkRoutes(network_id: string): Promise<IOptimusNetworkRoute[]> {
	return client(`/api/v1/pages/optimus-v2/routes/${network_id}`)
}
